import type React from 'react';

import { usePetitionDetailsPageData, usePetitionDetailsPageUserData } from '#src/app/pages/petitionDetails/pageContext';
import {
	usePageScrollPosition,
	usePromoteCtaDisplayed,
	useUnauthenticatedSignForm,
} from '#src/app/pages/petitionDetails/shared/hooks';
import type { PageScrollPosition } from '#src/app/pages/petitionDetails/shared/hooks';

import { PetitionStatusHeader } from '../PetitionStatusHeader';
import { SignatureStatsClosed, SignatureStatsVictory } from '../Stats';

import { MobileComboButton } from './MobileComboButton';
import { MobileRespondButton } from './MobileRespondButton';
import { MobileShareButton } from './MobileShareButton';
import { MobileSignButton } from './MobileSignButton';

export type ActionPanelVariant = 'v1' | 'v2';
/**
 * `inline-then-sticky` (the default) keeps the in-flow block and docks the bar once the viewer has
 * scrolled past it. `sticky-sign` (video-first hero) drops the in-flow block and shows the bar from
 * first paint, carrying the sign CTA alone. Neither ever puts two sign CTAs on screen at once.
 */
export type ActionPanelLayout = 'inline-then-sticky' | 'sticky-sign';

type Props = {
	layout?: ActionPanelLayout;
	sticky: boolean;
	variant: ActionPanelVariant;
};

const calculateVisibility = (
	layout: ActionPanelLayout,
	sticky: boolean,
	{ beforeSuggestedPetitions, afterDescription, afterMobileActionButton }: PageScrollPosition,
): boolean => {
	if (!sticky) {
		// `sticky-sign` replaces the in-flow block outright, so only the default layout renders it.
		return layout === 'inline-then-sticky';
	}

	/**
	 * The standard sticky button appears after the non-sticky button. `sticky-sign`, which docks from
	 * page load, starts visible instead.
	 * !afterDescription condition allows sticky button to show in petitions with short descriptions
	 */
	const hasReachedStickyStart = layout !== 'inline-then-sticky' || !!afterMobileActionButton;

	return hasReachedStickyStart && (beforeSuggestedPetitions || !afterDescription);
};

/**
 * Whether this render site paints the share affordance at all. `sticky-sign` carries the sign CTA
 * alone, dropping the affordance at both render sites.
 */
const rendersShareAffordance = (layout: ActionPanelLayout): boolean => layout !== 'sticky-sign';

export const MobileActionButton: React.FC<Props> = ({ layout = 'inline-then-sticky', sticky, variant }) => {
	const usePageScrollPositionContext = usePageScrollPosition();
	const {
		data: {
			petition: { status },
		},
	} = usePetitionDetailsPageData();
	const {
		data: { decisionMaker, signed },
	} = usePetitionDetailsPageUserData();
	const promoteCtaDisplayed = usePromoteCtaDisplayed();
	const {
		data: { enabled: unauthenticatedSignFormEnabled },
	} = useUnauthenticatedSignForm();

	const visible = calculateVisibility(layout, sticky, usePageScrollPositionContext);

	if (decisionMaker) {
		return <MobileRespondButton visible={visible} sticky={sticky} />;
	}

	if (status === 'CLOSED') {
		return (
			<div className="mb-6 flex flex-col gap-5">
				{!sticky && (
					<>
						<PetitionStatusHeader />
						<div className="text-center">
							<SignatureStatsClosed />
						</div>
					</>
				)}
				<MobileShareButton visible={visible} sticky={sticky} hideSocialProof />
			</div>
		);
	}

	if (status === 'VICTORY') {
		return (
			<div className="mb-6 flex flex-col gap-5">
				{!sticky && (
					<>
						<PetitionStatusHeader />
						<SignatureStatsVictory textAlign="center" />
					</>
				)}
				<MobileShareButton visible={visible} sticky={sticky} hideSocialProof />
			</div>
		);
	}

	if (signed && !unauthenticatedSignFormEnabled) {
		return promoteCtaDisplayed ? (
			<MobileComboButton visible={visible} sticky={sticky} />
		) : (
			<MobileShareButton visible={visible} sticky={sticky} />
		);
	}

	return (
		<MobileSignButton
			rendersShareAffordance={rendersShareAffordance(layout)}
			visible={visible}
			sticky={sticky}
			variant={variant}
		/>
	);
};
